import { adaptGraftWebsocketUrlForRuntime } from './adapt-graft-websocket-url.util'
import { buildSameOriginGraftUrl } from './build-same-origin-graft-url.util'
import { isSameOriginGraftPath } from './is-same-origin-graft-path.util'
import { readInternalGraftWebserviceUrl } from './read-internal-graft-webservice-url.util'

const trimEnv = (value: string | undefined) => {
  const trimmed = value?.trim()

  return trimmed || undefined
}

const pickGraftWebserviceUrl = (webValue: string | undefined, legacyValue: string | undefined): string | undefined =>
  trimEnv(webValue) ?? trimEnv(legacyValue)

/** Ensure the graft websocket URL ends with /ws (gg ingress path). */
const withWsPath = (url: string): string => {
  const trimmed = url.replace(/\/+$/, '')
  return trimmed.endsWith('/ws') ? trimmed : `${trimmed}/ws`
}

/**
 * Resolves graft websocket URLs for browser and server bundles.
 * Uses static process.env access so Next.js can inline NEXT_PUBLIC_* values on the client.
 */
const GRAFT_WEBSERVICE_URLS = {
  CONFERENCE: pickGraftWebserviceUrl(
    process.env.NEXT_PUBLIC_WEB_GRAFT_WEBSERVICE_CONFERENCE_URL,
    process.env.NEXT_PUBLIC_GRAFT_WEBSERVICE_CONFERENCE_URL
  ),
  WORKSPACES: pickGraftWebserviceUrl(
    process.env.NEXT_PUBLIC_WEB_GRAFT_WEBSERVICE_WORKSPACES_URL,
    process.env.NEXT_PUBLIC_GRAFT_WEBSERVICE_WORKSPACES_URL
  ),
  PROJECTS: pickGraftWebserviceUrl(
    process.env.NEXT_PUBLIC_WEB_GRAFT_WEBSERVICE_PROJECTS_URL,
    process.env.NEXT_PUBLIC_GRAFT_WEBSERVICE_PROJECTS_URL
  ),
  GATEWAYS: pickGraftWebserviceUrl(
    process.env.NEXT_PUBLIC_WEB_GRAFT_WEBSERVICE_GATEWAYS_URL,
    process.env.NEXT_PUBLIC_GRAFT_WEBSERVICE_GATEWAYS_URL
  ),
  ARTIFACT: pickGraftWebserviceUrl(
    process.env.NEXT_PUBLIC_WEB_GRAFT_WEBSERVICE_ARTIFACT_URL,
    process.env.NEXT_PUBLIC_GRAFT_WEBSERVICE_ARTIFACT_URL
  ),
  USERS: pickGraftWebserviceUrl(
    process.env.NEXT_PUBLIC_WEB_GRAFT_WEBSERVICE_USERS_URL,
    process.env.NEXT_PUBLIC_GRAFT_WEBSERVICE_USERS_URL
  ),
  REGUSERS: pickGraftWebserviceUrl(
    process.env.NEXT_PUBLIC_WEB_GRAFT_WEBSERVICE_REGUSERS_URL,
    process.env.NEXT_PUBLIC_GRAFT_WEBSERVICE_REGUSERS_URL
  ),
  ACTIVITY: pickGraftWebserviceUrl(
    process.env.NEXT_PUBLIC_WEB_GRAFT_WEBSERVICE_ACTIVITY_URL,
    process.env.NEXT_PUBLIC_GRAFT_WEBSERVICE_ACTIVITY_URL
  ),
  MAIL: pickGraftWebserviceUrl(
    process.env.NEXT_PUBLIC_WEB_GRAFT_WEBSERVICE_MAIL_URL,
    process.env.NEXT_PUBLIC_GRAFT_WEBSERVICE_MAIL_URL
  ),
  MEMBERSHIPS: pickGraftWebserviceUrl(
    process.env.NEXT_PUBLIC_WEB_GRAFT_WEBSERVICE_MEMBERSHIPS_URL,
    process.env.NEXT_PUBLIC_GRAFT_WEBSERVICE_MEMBERSHIPS_URL
  ),
  SEARCH: pickGraftWebserviceUrl(
    process.env.NEXT_PUBLIC_WEB_GRAFT_WEBSERVICE_SEARCH_URL,
    process.env.NEXT_PUBLIC_GRAFT_WEBSERVICE_SEARCH_URL
  ),
  MODULES: pickGraftWebserviceUrl(
    process.env.NEXT_PUBLIC_WEB_GRAFT_WEBSERVICE_MODULES_URL,
    process.env.NEXT_PUBLIC_GRAFT_WEBSERVICE_MODULES_URL
  ),
} as const

type GraftWebserviceName = keyof typeof GRAFT_WEBSERVICE_URLS

/** True when a public URL is set. Same value on the server and in the browser. */
export function hasConfiguredGraftWebservice(service: string): boolean {
  return Boolean(GRAFT_WEBSERVICE_URLS[service as GraftWebserviceName])
}

/**
 * Deployed portals configure a relay path (`/graft-ws/<service>`): the browser opens the
 * socket on its own origin and the portal server forwards it to the internal backend,
 * while server bundles keep using the internal URL directly. An absolute URL is still
 * honoured as-is, which is how local setups point straight at a backend.
 */
export function readGraftWebserviceUrl(service: string): string | undefined {
  const configured = GRAFT_WEBSERVICE_URLS[service as GraftWebserviceName]

  if (isSameOriginGraftPath(configured)) {
    const url =
      typeof window === 'undefined' ? readInternalGraftWebserviceUrl(service) : buildSameOriginGraftUrl(configured)

    return url ? withWsPath(url) : undefined
  }

  const adapted = adaptGraftWebsocketUrlForRuntime(configured)

  return adapted ? withWsPath(adapted) : undefined
}
